import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Users, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { HostBottomNav } from '../../components/HostBottomNav';
import { BellIcon } from '../../components/BellIcon';
import { hostCardStyle } from '../../components/HostSettingsCard';
import { HostListingPreviewCard } from '../../components/host-onboarding/HostListingPreviewCard';
import { buildAirbnbListingUniversalLink, isValidAirbnbListingUrl, normalizeHostUrl } from '../../utils/hostOnboarding';
import { buildHostInviteMessage } from '../../utils/hostInviteCopy';
import { copyTextToClipboard } from '../../utils/copyText';
import { MessageHostCoachModal } from '../../components/host/MessageHostCoachModal';
import { useBackTarget } from '../../hooks/useBackTarget';
import { trackInviteShared } from '../../utils/airbridge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

const cardStyle = {
  ...hostCardStyle,
  width: '100%',
  padding: '14px 14px 16px',
  display: 'grid',
  gap: 10,
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 16,
  border: '1px solid var(--card-border)',
  background: 'var(--surface)',
  fontFamily: font,
  fontSize: 17,
  color: 'var(--text)',
  textAlign: 'left',
  outline: 'none',
  boxShadow: 'var(--premium-shadow-nested)',
};

const badgeStyle = (active) => ({
  borderRadius: 9999,
  padding: '6px 10px',
  background: active ? 'var(--status-success-surface)' : 'var(--surface-2)',
  color: active ? 'var(--status-success-text)' : 'var(--text-2)',
  fontFamily: font,
  fontSize: 13,
  fontWeight: 700,
});

const cleanListingTitle = (value) => (value || 'Airbnb listing').split('·')[0].trim();

const CircledIcon = ({ Icon }) => (
  <div style={{ width: 50, height: 50, borderRadius: '50%', background: 'var(--status-success-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
    <Icon size={24} color="var(--status-success-text)" strokeWidth={1.75} />
  </div>
);

const InviteHostsIcon = () => <CircledIcon Icon={Users} />;
const RateBoostIcon = () => <CircledIcon Icon={TrendingUp} />;

const formatCooldown = (value) => {
  if (!value) return 'Ready now';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return `${date.toLocaleDateString('en-GB', { timeZone: 'Europe/Athens' })} ${date.toLocaleTimeString('en-GB', { timeZone: 'Europe/Athens', hour: '2-digit', minute: '2-digit' })}`;
};

export default function HostReferralInfoPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const [overview, setOverview] = useState(null);
  const [listingUrl, setListingUrl] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewReadyUrl, setPreviewReadyUrl] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pendingShareInvite, setPendingShareInvite] = useState(null);
  const previewRequestRef = useRef(0);
  const celebratedRef = useRef(false);

  const listingValid = useMemo(() => isValidAirbnbListingUrl(listingUrl), [listingUrl]);
  const listingError = touched && !listingValid ? 'Enter a valid Airbnb listing' : submitError;

  const loadOverview = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/host/listing-invites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to load invite progress');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  // Fire the one-shot 2% unlock confetti the first time the host lands here
  // after the 2nd invited host has joined Allure. Backend gates this via
  // `celebrate_unlock` in the overview response; we immediately ack so it
  // never re-fires. Guarded by celebratedRef in case React re-renders while
  // the ack is in flight.
  useEffect(() => {
    if (!overview?.celebrate_unlock || celebratedRef.current) return;
    celebratedRef.current = true;

    const fireConfetti = () => {
      const baseOpts = {
        spread: 70,
        startVelocity: 45,
        ticks: 220,
        gravity: 0.9,
        scalar: 1.05,
        colors: ['#04A628', '#FFD53F', '#FF6BAA', '#7A5CFF', '#3FB6FF'],
        zIndex: 9999,
      };
      // Two staggered bursts from bottom corners for a fuller cascade.
      confetti({ ...baseOpts, particleCount: 90, angle: 60, origin: { x: 0.05, y: 0.95 } });
      confetti({ ...baseOpts, particleCount: 90, angle: 120, origin: { x: 0.95, y: 0.95 } });
      window.setTimeout(() => {
        confetti({ ...baseOpts, particleCount: 60, angle: 90, origin: { x: 0.5, y: 0.85 } });
      }, 220);
    };

    try { fireConfetti(); } catch { /* confetti is decorative — never block */ }

    (async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/host/listing-invites/celebrate-unlock/ack`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } catch {
        /* silent — the flag will simply re-fire next open, which is fine */
      }
    })();
  }, [overview?.celebrate_unlock]);

  const fetchPreview = useCallback(async (rawUrl) => {
    const normalizedUrl = normalizeHostUrl(rawUrl);
    if (!isValidAirbnbListingUrl(normalizedUrl)) {
      setPreview(null);
      setPreviewReadyUrl('');
      return;
    }

    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    try {
      const response = await axios.post(`${API}/host/listing-preview`, { listing_url: normalizedUrl });
      if (previewRequestRef.current !== requestId) return;
      setPreview(response.data?.preview || null);
      setPreviewReadyUrl(normalizedUrl);
    } catch {
      if (previewRequestRef.current === requestId) {
        setPreview(null);
        setPreviewReadyUrl(normalizedUrl);
      }
    }
  }, []);

  const handleGenerateInvite = async () => {
    const normalizedUrl = normalizeHostUrl(listingUrl);
    setTouched(true);
    setSubmitError('');
    if (!isValidAirbnbListingUrl(normalizedUrl)) {
      toast.error('Enter a valid Airbnb listing');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/host/listing-invites`,
        { listing_url: normalizedUrl },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Invite generated');
      setListingUrl('');
      setPreview(null);
      setPreviewReadyUrl('');
      setTouched(false);
      setSubmitError('');
      await loadOverview();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Unable to generate invite';
      setSubmitError(detail);
      toast.error(detail);
      await loadOverview();
    } finally {
      setGenerating(false);
    }
  };

  const buildInvitePayload = (invite) => {
    // The shared message no longer carries a tracking URL — hosts pointed
    // at `allurexp. com` alone are less likely to be filtered by Airbnb
    // chat and less likely to look scary to the recipient. Attribution
    // still works internally because the invited Airbnb listing_id is
    // stored on `host_listing_invites` and matched when the recipient
    // joins with that same listing.
    const inviteMessage = buildHostInviteMessage({
      firstName: invite.listing_host_first_name || '',
    });
    return { inviteMessage };
  };

  const handleSmartShareInvite = (invite) => {
    // Tapping "Share invite" opens the Airbnb coach modal. The invite is
    // copied inside the modal on "Copy And Share" tap, which also opens the
    // Airbnb listing URL so the host can paste under "Message host".
    setPendingShareInvite(invite);
  };

  const handleCoachConfirm = async () => {
    const invite = pendingShareInvite;
    setPendingShareInvite(null);
    if (!invite) return;

    const { inviteMessage } = buildInvitePayload(invite);
    const listingDeepLink = buildAirbnbListingUniversalLink(invite.listing_url || '');

    const copied = await copyTextToClipboard(inviteMessage);
    if (!copied) {
      if (navigator.share) {
        try { await navigator.share({ text: inviteMessage }); } catch { return; }
      } else {
        toast.error('Could not copy invite. Please try again.');
        return;
      }
    }

    trackInviteShared({
      inviteId: invite.id,
      inviteType: 'host_to_host',
      channel: listingDeepLink ? 'airbnb_deeplink' : 'clipboard',
      extra: { variant: invite.variant || 'a' },
    });

    if (!listingDeepLink) {
      toast.success('Invite copied. Open the Airbnb listing and paste it under “Message host”.');
      return;
    }

    toast.success('Invite copied — opening Airbnb.');
    // _system forces Capacitor to open in the external browser (Chrome on Android),
    // bypassing Android App Links so the Airbnb native app never hijacks the URL.
    window.open(listingDeepLink, '_system');
  };

  const handleCoachCancel = () => setPendingShareInvite(null);

  const currentRate = overview?.current_commission_rate;
  const rateLabel = currentRate != null ? `${Number(currentRate)}%` : '0.5%';
  const unlocked = Boolean(overview?.commission_unlocked);

  const infoCards = [
    {
      key: 'invite',
      title: 'Invite 2 nearby hosts',
      description: 'Copy an Airbnb listing URL from your area and paste it below',
      Icon: InviteHostsIcon,
    },
    {
      key: 'rate',
      title: unlocked ? '2% unlocked → and beyond' : `${rateLabel} → 2% → and beyond`,
      description: unlocked
        ? "You've unlocked the 2% tier — it's yours for good. A new tier will open later — once Allure scales — and 2% hosts will be first in line."
        : `You're currently earning ${rateLabel} of your guests' spending. Unlock 2% by inviting hosts whose listing becomes active. A new tier will open later — once Allure scales — and 2% hosts will be first in line.`,
      Icon: RateBoostIcon,
    },
  ];

  return (
    <div className="app-shell" data-testid="host-referral-info-page" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div style={{ padding: '18px 18px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button data-testid="host-referral-back-button" onClick={goBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ChevronLeft size={40} color="var(--text)" strokeWidth={1.6} />
        </button>
        <button data-testid="host-referral-bell-button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <BellIcon />
        </button>
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, paddingTop: 4 }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 116, height: 102, objectFit: 'contain' }} />

        <h1 data-testid="host-referral-heading" style={{ fontFamily: font, fontSize: 26, fontWeight: 700, lineHeight: 0.96, color: 'var(--text)', textAlign: 'center', margin: 0 }}>
          Grow your earnings easily!
        </h1>

        {infoCards.map(({ key, title, description, Icon }) => (
          <div key={key} data-testid={`host-referral-card-${key}`} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <Icon />
              <p style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1 }}>{title}</p>
            </div>
            <p style={{ fontFamily: font, fontSize: 17, color: 'var(--text)', lineHeight: 1.15, margin: 0 }}>{description}</p>
          </div>
        ))}

        {overview && !unlocked ? (
          <div data-testid="host-referral-urgency-card" style={{ ...cardStyle, gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
              <p data-testid="host-referral-progress-line" style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                {Math.max(0, Math.min(overview.invite_target || 2, (overview.invite_target || 2) - (overview.remaining_generations ?? (overview.invite_target || 2))))} of {overview.invite_target || 2} invites generated
              </p>
              <span style={badgeStyle((overview.joined_invites_count || 0) >= (overview.joined_target || 2))}>
                {overview.joined_invites_count || 0} of {overview.joined_target || 2} joined
              </span>
            </div>
            <p data-testid="host-referral-scarcity-line" style={{ fontFamily: font, fontSize: 15.5, color: 'var(--text-2)', lineHeight: 1.3, margin: 0 }}>
              ⚠ Once nearby listings are invited by other hosts, they&apos;re gone — hosts with no one left to invite stay at their current rate permanently.
            </p>
          </div>
        ) : null}

        <div data-testid="host-referral-generator-card" style={cardStyle}>
          <p style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Paste Airbnb listing URL</p>
          <input
            type="text"
            value={listingUrl}
            onChange={(event) => {
              const nextValue = event.target.value;
              setListingUrl(nextValue);
              setSubmitError('');
              if (previewReadyUrl && normalizeHostUrl(nextValue) !== previewReadyUrl) {
                setPreview(null);
              }
            }}
            onBlur={() => {
              setTouched(true);
              void fetchPreview(listingUrl);
            }}
            onPaste={(event) => {
              const pastedUrl = event.clipboardData?.getData('text') || '';
              window.requestAnimationFrame(() => {
                void fetchPreview(pastedUrl || event.target.value || listingUrl);
              });
            }}
            placeholder="e.g. airbnb.com/rooms/321..."
            data-testid="host-referral-url-input"
            autoComplete="off"
            spellCheck={false}
            style={inputStyle}
          />
          <HostListingPreviewCard preview={preview} />
          {listingError ? <p data-testid="host-referral-url-error" style={{ fontFamily: font, fontSize: 14, color: 'var(--accent-text-soft)', margin: 0 }}>{listingError}</p> : null}
          <button
            type="button"
            data-testid="host-referral-generate-button"
            onClick={handleGenerateInvite}
            disabled={generating || loading}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: 9999,
              padding: '14px 20px',
              background: 'var(--accent-purple)',
              color: '#FFFFFF',
              fontFamily: font,
              fontSize: 22,
              fontWeight: 700,
              cursor: generating || loading ? 'default' : 'pointer',
              opacity: generating || loading ? 0.7 : 1,
              boxShadow: 'var(--premium-shadow-main)',
            }}
          >
            {generating ? 'Generating…' : 'Generate invite'}
          </button>
        </div>

        <div data-testid="host-referral-invites-card" style={{ width: '100%', display: 'grid', gap: 12, marginBottom: 20 }}>
          <p style={{ fontFamily: font, fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: 0 }}>Invites</p>
          {loading ? <p style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)', margin: 0 }}>Loading…</p> : null}
          {!loading && !(overview?.invites || []).length ? (
            <p data-testid="host-referral-empty-state" style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)', margin: 0 }}>No invite generations yet.</p>
          ) : null}
          {(overview?.invites || []).map((invite) => (
            <div key={invite.id} data-testid={`host-referral-invite-${invite.id}`} className="premium-card" style={{ width: '100%', padding: 12, display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 88, height: 74, borderRadius: 18, overflow: 'hidden', background: 'var(--surface-2)', flexShrink: 0 }}>
                  {invite.listing_image ? <img src={invite.listing_image} alt={cleanListingTitle(invite.listing_title)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                </div>
                <div style={{ minWidth: 0, display: 'grid', gap: 6, flex: 1 }}>
                  <p style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.15 }}>{cleanListingTitle(invite.listing_title)}</p>
                  <p style={{ fontFamily: font, fontSize: 15, color: 'var(--text-2)', margin: 0, lineHeight: 1.2 }}>{invite.listing_location_label || 'Location pending'}</p>
                </div>
                <span data-testid={`host-referral-invite-status-${invite.id}`} style={badgeStyle(['joined', 'early'].includes(invite.status))}>
                  {['joined', 'early'].includes(invite.status) ? 'Joined' : 'Not joined'}
                </span>
              </div>
              <button
                type="button"
                data-testid={`host-referral-share-button-${invite.id}`}
                onClick={() => handleSmartShareInvite(invite)}
                style={{ border: 'none', borderRadius: 9999, padding: '12px 14px', background: 'var(--solid-cta-bg)', color: 'var(--solid-cta-text)', cursor: 'pointer', fontFamily: font, fontSize: 18, fontWeight: 700 }}
              >
                Share invite
              </button>
            </div>
          ))}
        </div>
      </div>

      <HostBottomNav />

      <MessageHostCoachModal
        open={Boolean(pendingShareInvite)}
        onConfirm={handleCoachConfirm}
        onCancel={handleCoachCancel}
      />
    </div>
  );
}